import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../services/api";
import toast from "react-hot-toast";

export const useDealers = (params?: {
  limit?: number;
  skip?: number;
  search?: string;
  status?: string;
  marketingFilter?: 'all' | 'assigned' | 'unassigned';
  imageFilter?: 'all' | 'missing_images';
  categoryFilter?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  automationFilter?: 'automation_only' | 'non_automation';
}) => {
  return useQuery({
    queryKey: ["dealers", params],
    queryFn: () => apiService.getDealers(params),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export const useUpdateDealerMetrics = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiService.updateDealerMetrics(),
    onSuccess: (data) => {
      toast.success(data.message || 'Dealer metrics update triggered successfully!');
      queryClient.invalidateQueries({ queryKey: ["dealers"] });
      queryClient.invalidateQueries({ queryKey: ["dealerStats"] });
    },
    onError: (error: any) => {
      toast.error(`Failed to trigger update: ${error.response?.data?.message || error.message}`);
    },
  });
};

export const useDealer = (id: string) => {
  return useQuery({
    queryKey: ["dealer", id],
    queryFn: () => apiService.getDealer(id),
    enabled: !!id,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1000,
  });
};

export const useDealerStats = () => {
  return useQuery({
    queryKey: ["dealerStats"],
    queryFn: () => apiService.getDealerStats(),
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export const useUpdateDealerContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dealerId,
      contactData,
    }: {
      dealerId: string;
      contactData: {
        email?: string;
        phonePrefix?: string;
        phoneNumber?: string;
      };
    }) => apiService.updateDealerContact(dealerId, contactData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dealers"] });
      queryClient.invalidateQueries({ queryKey: ["dealer"] });
    },
  });
};

export const useChangeDealerPassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dealerId,
      newPassword,
    }: {
      dealerId: string;
      newPassword: string;
    }) => apiService.changeDealerPassword(dealerId, newPassword),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dealers"] });
      queryClient.invalidateQueries({ queryKey: ["dealer"] });
    },
  });
};

export const useUpdateStoreStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dealerId,
      storeId,
      status,
    }: {
      dealerId: string;
      storeId: string;
      status: 'active' | 'pending' | 'inactive';
    }) => apiService.updateStoreStatus(dealerId, storeId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dealers"] });
      queryClient.invalidateQueries({ queryKey: ["dealer"] });
    },
  });
};

export const useUpdateStoreImages = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dealerId,
      storeId,
      images,
    }: {
      dealerId: string;
      storeId: string;
      images: { coverImage?: string; logoImage?: string };
    }) => apiService.updateStoreImages(dealerId, storeId, images),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dealers"] });
      queryClient.invalidateQueries({ queryKey: ["dealer"] });
    },
  });
};

export const useUpdateStoreCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dealerId,
      storeId,
      data,
    }: {
      dealerId: string;
      storeId: string;
      data: { storeCategory?: string; storeSubcategory?: string };
    }) => apiService.updateStoreCategory(dealerId, storeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dealers"] });
      queryClient.invalidateQueries({ queryKey: ["dealer"] });
    },
  });
};

export const useUpdateDealerZipCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dealerId, zipCode }: { dealerId: string; zipCode: string }) =>
      apiService.updateDealerZipCode(dealerId, zipCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dealers"] });
      queryClient.invalidateQueries({ queryKey: ["dealer"] });
    },
  });
};

export const useUpdateStoreZipCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dealerId, storeId, zipCode }: { dealerId: string; storeId: string; zipCode: string }) =>
      apiService.updateStoreZipCode(dealerId, storeId, zipCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dealers"] });
      queryClient.invalidateQueries({ queryKey: ["dealer"] });
    },
  });
};

export const useUpdateDealerCountry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dealerId, country }: { dealerId: string; country: string }) =>
      apiService.updateDealerCountry(dealerId, country),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dealers"] });
      queryClient.invalidateQueries({ queryKey: ["dealer"] });
    },
  });
};

export const useUpdateStoreCountry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dealerId, storeId, country }: { dealerId: string; storeId: string; country: string }) =>
      apiService.updateStoreCountry(dealerId, storeId, country),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dealers"] });
      queryClient.invalidateQueries({ queryKey: ["dealer"] });
    },
  });
};

export const useActivateDealerByAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dealerId,
      addPrepaidTopup,
    }: {
      dealerId: string;
      addPrepaidTopup?: boolean;
    }) => apiService.activateDealerByAdmin(dealerId, { addPrepaidTopup }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dealers"] });
      queryClient.invalidateQueries({ queryKey: ["dealer"] });
      queryClient.invalidateQueries({ queryKey: ["dealerStats"] });
    },
  });
};

export const useUpdateDealerSponsor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dealerId, sponsorId }: { dealerId: string; sponsorId: string }) =>
      apiService.updateDealerSponsor(dealerId, { sponsorId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dealers"] });
      queryClient.invalidateQueries({ queryKey: ["dealer"] });
    },
  });
};
